import { CompletedByHustlerItem } from './completed-by-hustler-item'
import styles from './completed-by-hustlers-feed.module.scss'
import { CompletedByHustlersResponse } from '../../dao/core-dao'
import { useCompletedByHustlers } from '../../hook/use-completed-by-hustlers'
import { Skeleton } from '../ui/skeleton'
import React, { useMemo } from 'react'

interface CompletedByHustlersFeedProps {
  workoutId: string
}

export const CompletedByHustlersFeed = ({ workoutId }: CompletedByHustlersFeedProps): React.ReactElement => {
  const query = useCompletedByHustlers(workoutId)
  const allData = useMemo(() => query.data?.pages.flatMap((page: CompletedByHustlersResponse) => page.data) ?? [], [query.data])
  const totalCount = query.data?.pages[0]?.totalSize ?? 0
  const hasMore = query.hasNextPage ?? false
  const isLoading = query.isLoading
  const isError = query.isError
  const refetch = query.refetch
  const isFetching = query.isFetchingNextPage

  return (
    <div className={styles.feedContainer}>
      <div className="flex items-center gap-2">
        <h2 className="text-xl font-evogria uppercase text-white">
          Completed by{' '}
          <span className="text-primary">
            {totalCount > 0 ? `${totalCount}` : ''} Hustler{totalCount !== 1 ? 's' : ''}
          </span>
        </h2>
      </div>

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {!isLoading && allData.length === 0 && !isError && (
        <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-8 text-center">
          <div className="text-lg font-bold text-white">
            BE THE FIRST HUSTLER TO <span className="text-primary">COMPLETE THIS WORKOUT</span> 🔥
          </div>
          <div className="mt-1 text-sm font-semibold text-white/70">SHOW THE COMMUNITY WHAT YOU'RE MADE OF!</div>
        </div>
      )}

      <div className="space-y-3">
        {allData.map((hustlerCompletion: CompletedByHustlersResponse['data'][number]) => (
          <CompletedByHustlerItem key={hustlerCompletion.completedWorkoutId} hustlerCompletion={hustlerCompletion} />
        ))}
      </div>

      {hasMore && (
        <div className="pt-2">
          <button
            type="button"
            onClick={() => query.fetchNextPage()}
            disabled={isFetching}
            className="w-full rounded-full border border-white/20 px-4 py-2 text-sm font-semibold text-white transition-all hover:border-primary hover:bg-primary/10 disabled:opacity-50"
          >
            {isFetching ? 'Loading...' : 'Load more hustlers'}
          </button>
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          Failed to load completions.{' '}
          <button type="button" className="underline" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}
    </div>
  )
}
